import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Bell, Check } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Relative time label: "just now", "1h ago", "3h ago", "11h ago"
 */
function timeAgo(isoString) {
  if (!isoString) return null;
  const diff = Date.now() - new Date(isoString).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

/**
 * SendReminderButton — strictly additive, secondary/subtle reminder trigger.
 *
 * On mount, checks the last nudge time via GET /api/nudge/last-sent.
 * If within 12h cooldown, shows a softened "Sent Xh ago" state.
 * Otherwise shows the normal action button.
 *
 * Props:
 *  - entityType:  "inquiry" | "booking"
 *  - entityId:    the inquiry_id or booking_id
 *  - nudgeState:  "proposal_pending" | "deposit_required" | "final_payment_required" | "message_unread"
 *  - label:       microcopy (e.g. "Remind to respond")
 *  - className:   optional extra classes
 *  - size:        "sm" (icon+text) | "icon" (icon-only with tooltip)
 */
export const SendReminderButton = ({
  entityType,
  entityId,
  nudgeState,
  label = 'Send Reminder',
  className = '',
  size = 'sm',
}) => {
  const [sending, setSending] = useState(false);
  const [lastSentAt, setLastSentAt] = useState(null);
  const [justSent, setJustSent] = useState(false);

  // 12h cooldown window in ms
  const COOLDOWN_MS = 12 * 60 * 60 * 1000;
  const isInCooldown = lastSentAt && (Date.now() - new Date(lastSentAt).getTime()) < COOLDOWN_MS;

  // Check last nudge time on mount
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token || !entityId) return;

    axios.get(`${API}/nudge/last-sent`, {
      params: { entity_type: entityType, entity_id: entityId, nudge_state: nudgeState },
      headers: { Authorization: `Bearer ${token}` },
    })
    .then(res => { if (!cancelled && res.data?.last_sent_at) setLastSentAt(res.data.last_sent_at); })
    .catch(() => {}); // silent — non-critical

    return () => { cancelled = true; };
  }, [entityType, entityId, nudgeState]);

  const handleSend = useCallback(async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (sending || justSent || isInCooldown) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API}/nudge`,
        { entity_type: entityType, entity_id: entityId, nudge_state: nudgeState },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.success) {
        toast.success('Reminder sent');
        setLastSentAt(res.data.sent_at || new Date().toISOString());
        setJustSent(true);
      } else {
        if (res.data?.last_sent_at) setLastSentAt(res.data.last_sent_at);
        toast.info(res.data?.error || 'Reminder already sent recently');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.response?.data?.error || 'Could not send reminder';
      toast.error(msg);
    } finally {
      setSending(false);
    }
  }, [entityType, entityId, nudgeState, sending, justSent, isInCooldown]);

  const sentLabel = justSent ? 'Sent' : (isInCooldown ? `Sent ${timeAgo(lastSentAt)}` : null);

  if (size === 'icon') {
    return (
      <button
        type="button"
        onClick={handleSend}
        disabled={sending || isInCooldown}
        title={sentLabel || label}
        className={`h-8 w-8 rounded-lg flex items-center justify-center transition-colors
          ${isInCooldown || justSent
            ? 'bg-emerald-50/60 text-emerald-400 cursor-default'
            : 'bg-gray-50 hover:bg-gray-100 text-gray-400 hover:text-gray-600'}
          ${className}`}
        data-testid={`send-reminder-${entityType}-${entityId}`}
      >
        {isInCooldown || justSent
          ? <Check className="h-3.5 w-3.5" />
          : <Bell className={`h-3.5 w-3.5 ${sending ? 'animate-pulse' : ''}`} />
        }
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleSend}
      disabled={sending || isInCooldown}
      className={`inline-flex items-center gap-1.5 min-h-[40px] sm:min-h-0 sm:h-8 px-3 rounded-md text-xs font-medium transition-colors whitespace-nowrap shrink-0
        ${isInCooldown || justSent
          ? 'bg-gray-50 text-gray-400 cursor-default'
          : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}
        ${className}`}
      data-testid={`send-reminder-${entityType}-${entityId}`}
    >
      {isInCooldown || justSent
        ? <Check className="h-3.5 w-3.5 text-emerald-400" />
        : <Bell className={`h-3.5 w-3.5 ${sending ? 'animate-pulse' : ''}`} />
      }
      <span>{sentLabel || (sending ? 'Sending...' : label)}</span>
    </button>
  );
};

export default SendReminderButton;
